import classNames from 'classnames';
import type { PropsWithChildren } from 'react';
import { useId } from 'react';
import styled from 'styled-components';

import type { RGBColor } from '#src/style/types.ts';

const Root = styled.div`
  position: relative;
  isolation: isolate;
`;
const OutlineFilter = styled.svg`
  position: absolute;
`;

type Props = PropsWithChildren<{
  active: boolean;
  color: RGBColor;
  className?: string;
}>;

const GLOW_RADIUS = 8;

export const GlowCard = ({ active, children, color, className }: Props) => {
  const outlineId = `outline-${useId()}`;

  return (
    <Root
      style={
        {
          '--glow-color': color ? `rgba(${color}, 0.3)` : 'transparent',
          filter: active ? `url(#${outlineId})` : undefined,
        } as React.CSSProperties
      }
      className={classNames(className, { active })}
    >
      {!!active && (
        <OutlineFilter width="0" height="0">
          <filter id={outlineId}>
            {/* Stroke: dilate the shape */}
            <feMorphology
              operator="dilate"
              radius={0}
              in="SourceAlpha"
              result="expanded"
            />
            <feFlood floodColor="var(--glow-color)" result="strokeColor" />
            <feComposite
              in="strokeColor"
              in2="expanded"
              operator="in"
              result="stroke"
            />
            {/* Glow: dilate more than stroke, then blur */}
            <feMorphology
              operator="dilate"
              radius={GLOW_RADIUS}
              in="SourceAlpha"
              result="glowExpanded"
            />
            <feGaussianBlur
              stdDeviation={GLOW_RADIUS / 2}
              in="glowExpanded"
              result="glowBlurred"
            />
            <feFlood floodColor="var(--glow-color)" result="glowColor" />
            <feComposite
              in="glowColor"
              in2="glowBlurred"
              operator="in"
              result="glow"
            />
            <feMerge>
              <feMergeNode in="glow" />
              <feMergeNode in="stroke" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </OutlineFilter>
      )}
      {children}
    </Root>
  );
};
